import React, { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import { 
  FaSearch, FaChevronDown, FaChevronUp, FaTrashAlt, FaMoneyBillWave, 
  FaPlus, FaTimes, FaEdit, FaPrint, FaLock, FaFileExport 
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet-async";
import axiosInstance from "../../api/axiosInstance";
import Pagination from "../Pagination/Pagination";
import "./AddNormalDonation.css";

const DonorDropdown = ({ value, placeholder, donors, searchKey, onSelect, onClear, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = donors.filter((d) => (d[searchKey] || "").toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setHighlighted(0); }, [search, open]);
  
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector('.normal-donation-list-in-admin-drop-highlighted');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0 && highlighted < filtered.length) { onSelect(filtered[highlighted]); setOpen(false); setSearch(""); }
    }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="normal-donation-list-in-admin-filter-wrap" ref={ref} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div className={`normal-donation-list-in-admin-filter-trigger ${open ? "active" : ""}`} onClick={() => { if(!disabled) { setOpen((o) => !o); setSearch(""); } }}>
        <span className="normal-donation-list-in-admin-filter-text" style={{ color: value ? "#2c3e50" : "#334155", fontWeight: value ? "600" : "400" }}>{value || placeholder}</span>
        <div className="normal-donation-list-in-admin-trigger-icons">
          {value && !disabled && <FaTimes className="normal-donation-list-in-admin-clear-icon" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear Selection" />}
          <FaChevronDown size={10} style={{ color: "#64748b" }} />
        </div>
      </div>
      {open && (
        <div className="normal-donation-list-in-admin-filter-menu" onClick={(e) => e.stopPropagation()}>
          <input type="text" placeholder={`Search ${placeholder.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleKey} autoFocus />
          <ul ref={listRef}>
            {filtered.length > 0 ? filtered.map((d, i) => (
              <li key={d._id} className={i === highlighted ? "normal-donation-list-in-admin-drop-highlighted" : ""} onMouseEnter={() => setHighlighted(i)} onClick={() => { onSelect(d); setOpen(false); setSearch(""); }}>
                {d[searchKey]}
              </li>
            )) : <li className="normal-donation-list-in-admin-drop-empty">No results found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

const DONATION_TYPES = ["General Donation (सामान्य दान)", "Shiksha Nek (शिक्षा दान)"];
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "NEFT", "RTGS"];

const AddNormalDonation = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(""); 

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [editDonationData, setEditDonationData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedDonationId, setExpandedDonationId] = useState(null); 
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [form, setForm] = useState({
    donationType: DONATION_TYPES[0], amount: "", date: new Date().toISOString().split("T")[0], mode: "Cash", refNo: "", receiptNo: ""
  });

  useEffect(() => { fetchDonors(); }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get("/api/donors");
      setDonors(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) { console.error(err); }
  };

  const activeDonors = useMemo(() => donors.filter((d) => !d.isDeleted), [donors]);

  const getMissingFieldsForGeneral = (donor) => {
    if (!donor) return [];
    const missing = [];
    if (!donor.name?.toString().trim()) missing.push("Full Name");
    if (!donor.fatherName?.toString().trim()) missing.push("Father's Name");
    if (!donor.mobileNo?.toString().trim()) missing.push("Mobile Number");
    if (!donor.state?.toString().trim()) missing.push("State");
    if (!donor.district?.toString().trim()) missing.push("District");
    if (!donor.currentAddress?.toString().trim()) missing.push("Current Address");
    if (!donor.permanentAddress?.toString().trim()) missing.push("Permanent Address");
    return missing;
  };

  const missingProfileFields = useMemo(() => {
    return getMissingFieldsForGeneral(selectedDonor);
  }, [selectedDonor]);

  const groupedDonors = useMemo(() => {
    let list = [];

    donors.forEach((d) => {
      if (d.isDeleted) return;

      const userNormalDonations = (d.donations || [])
        .filter((don) => {
          const dTypeLower = (don.donationType || "").toLowerCase();
          const isProtsahan = dTypeLower.includes("protsahan") || dTypeLower.includes("encouragement") || dTypeLower.includes("प्रोत्साहन");
          return !isProtsahan && (don.txnStatus === "Success" || !don.txnStatus);
        })
        .map((don) => ({
          ...don,
          donorId: d._id,
          memberId: d.memberId,
          name: d.name,
          fatherName: d.fatherName,
          occupation: d.occupation,
          mobileNo: d.mobileNo,
          email: d.email,
          aadharNo: d.aadharNo,
          panNo: d.panNo,
          permanentAddress: d.permanentAddress,
          currentAddress: d.currentAddress,
          state: d.state,
          district: d.district,
          isActive: don.isActive !== false
        }));

      if (userNormalDonations.length > 0) {
        const sortedDonations = userNormalDonations.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        list.push({
          ...d,
          normalDonationsList: sortedDonations,
          latestDonationDate: new Date(sortedDonations[0].date)
        });
      }
    });

    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      list = list.filter((l) => 
        (l.memberId || "").toLowerCase().includes(q) || 
        (l.name || "").toLowerCase().includes(q) || 
        (l.mobileNo || "").includes(q)
      );
    }

    if (typeFilter) {
      list = list.filter((l) => l.normalDonationsList.some((don) => don.donationType === typeFilter));
    }

    if (modeFilter) {
      list = list.filter((l) => l.normalDonationsList.some((don) => don.mode === modeFilter));
    }

    if (dateFilter) {
      list = list.filter((l) => 
        l.normalDonationsList.some((don) => {
          const d = new Date(don.date);
          const localDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return localDateStr === dateFilter;
        })
      );
    }

    return list.sort((a, b) => b.latestDonationDate - a.latestDonationDate);
  }, [donors, searchFilter, typeFilter, modeFilter, dateFilter]);

  const paginatedDonors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return groupedDonors.slice(startIndex, startIndex + itemsPerPage);
  }, [groupedDonors, currentPage, itemsPerPage]);

  const handleDonorSelect = (donor) => setSelectedDonor(donor);
  const handleClearDonor = () => {
    setSelectedDonor(null); setEditDonationData(null);
    setForm({ donationType: DONATION_TYPES[0], amount: "", date: new Date().toISOString().split("T")[0], mode: "Cash", refNo: "", receiptNo: "" });
  };

  const toggleMainRow = (donorId) => {
    setExpandedRow(expandedRow === donorId ? null : donorId);
    setExpandedDonationId(null); 
  };

  const handleProcessPayment = async () => {
    if (!selectedDonor) return Swal.fire("Warning", "Please select a donor first.", "warning");
    
    if (missingProfileFields.length > 0) {
      return Swal.fire(
        "Incomplete Donor Profile",
        `This donor is missing the following required field(s): ${missingProfileFields.join(", ")}. Please edit the donor profile in Donor Management first before recording this donation.`,
        "warning"
      );
    }

    if (!form.amount || !form.date) return Swal.fire("Warning", "Amount and Date are required.", "warning");
    
    if (form.mode !== "Cash" && (!form.refNo || form.refNo.trim() === "")) {
      return Swal.fire("Warning", "Reference / Cheque Number is mandatory for all non-Cash payment modes.", "warning");
    }

    try {
      if (editDonationData) {
        await axiosInstance.put(`/api/donors/${editDonationData.donorId}/donate/${editDonationData.donationId}`, form);
        Swal.fire("Updated", "Donation updated successfully", "success");
      } else {
        await axiosInstance.post(`/api/donors/${selectedDonor._id}/donate`, form);
        Swal.fire("Success", "Donation added successfully", "success");
      }
      handleClearDonor(); setShowAddForm(false); fetchDonors();
    } catch (err) { Swal.fire("Error", "Failed to process donation", "error"); }
  };

  const handleEditDonation = (donation) => {
    const donor = donors.find((d) => d._id === donation.donorId);
    setSelectedDonor(donor);
    setForm({
      donationType: donation.donationType, amount: donation.amount,
      date: new Date(donation.date).toISOString().split("T")[0], mode: donation.mode, refNo: donation.refNo || "", receiptNo: donation.receiptNo || ""
    });
    setEditDonationData({ donorId: donation.donorId, donationId: donation._id });
    setShowAddForm(true); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteDonation = async (donorId, donationId) => {
    const res = await Swal.fire({ title: "Remove Donation?", text: "This action cannot be undone.", icon: "warning", showCancelButton: true, confirmButtonColor: "#e74c3c" });
    if (res.isConfirmed) {
      await axiosInstance.delete(`/api/donors/${donorId}/donate/${donationId}`);
      fetchDonors();
    }
  };

  const toggleDonorStatus = async (donorId, currentStatus) => {
    try {
      await axiosInstance.put(`/api/donors/${donorId}/status`, { isActive: !currentStatus });
      fetchDonors();
    } catch (err) {
      Swal.fire("Error", "Could not update donor status", "error");
    }
  };

  const toggleDonationStatus = async (donorId, donationId, currentStatus) => {
    try {
      await axiosInstance.put(`/api/donors/${donorId}/donate/${donationId}/status`, { isActive: !currentStatus });
      fetchDonors();
    } catch (err) {
      Swal.fire("Error", "Could not update status", "error");
    }
  };

  const handleExportExcel = async () => {
    if (groupedDonors.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Normal Donations Registry');

    worksheet.mergeCells('A1:M1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A2:M2');
    worksheet.getCell('A2').value = 'Donations Log & Statement (दान प्राप्ति विवरण सूची)';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A3:M3');
    worksheet.getCell('A3').value = `Search: ${searchFilter || 'All'}  |  Date: ${dateFilter || 'All'}  |  Type: ${typeFilter || 'All'}  |  Mode: ${modeFilter || 'All'}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    const headers = [
      'S.No', 'Member ID', 'Donor Name', 'Father Name', 'Contact Number', 
      'Current Address', 'Date', 'Year', 'Donation Type', 'Amount (₹)', 'Payment Mode', 'Reference Number', 'Status'
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Arial', size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    let serialNo = 1;
    groupedDonors.forEach((donor) => {
      donor.normalDonationsList.forEach((don) => {
        const year = new Date(don.date).getFullYear();
        const dateOnly = new Date(don.date).toLocaleDateString('en-IN'); 

        const row = worksheet.addRow([
          serialNo++,
          donor.memberId || 'N/A',
          donor.name,
          donor.fatherName || 'N/A',
          donor.mobileNo || 'N/A',
          donor.currentAddress || 'N/A',
          dateOnly, 
          year,
          don.donationType || 'N/A',
          don.amount,
          don.mode || 'CASH',
          don.refNo || 'N/A',
          don.isActive ? 'Active' : 'Disabled'
        ]);

        row.eachCell((cell, colIndex) => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.font = { name: 'Arial', size: 9 };
          
          if ([3, 4, 6, 9].includes(colIndex)) {
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          } else if (colIndex === 10) { 
            cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
            cell.numFmt = '₹#,##0.00';
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          }
        });

        const statusCell = row.getCell(13);
        statusCell.font = { bold: true, name: 'Arial', size: 9, color: { argb: don.isActive ? 'FF16A34A' : 'FF94A3B8' } };
      });
    });

    worksheet.columns = [
      { width: 8 },   
      { width: 14 },  
      { width: 22 },  
      { width: 20 },  
      { width: 15 },  
      { width: 35 },  
      { width: 14 },  
      { width: 10 },  
      { width: 25 },  
      { width: 15 },  
      { width: 18 },  
      { width: 22 },  
      { width: 12 }   
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Normal_Donations_Database.xlsx');
  };

  return (
    <>
      <Helmet>
        <title>Donations | Rajput Shiksha Kosh</title>
        <meta name="description" content="Manage Donations for the Rajput Shiksha Kosh organization." />
      </Helmet>
      
      <div className="normal-donation-list-in-admin-container">
        <h2 className="normal-donation-list-in-admin-title">
          <FaMoneyBillWave style={{ marginRight: "8px" }} /> Manage Donations
        </h2>

        <div className="normal-donation-list-in-admin-controls">
          <div className="normal-donation-list-in-admin-search-wrapper">
            <FaSearch className="normal-donation-list-in-admin-search-icon" />
            <input 
              type="text" 
              className="normal-donation-list-in-admin-search-input" 
              placeholder="Search ID, Name, or Mobile..." 
              value={searchFilter} 
              onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(1); }} 
            />
          </div>
          
          <div className="normal-donation-list-in-admin-right-controls">
            <input 
              type="date" 
              value={dateFilter} 
              onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} 
              className="normal-donation-list-in-admin-items-select" 
              title="Filter by Specific Date" 
            />
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }} className="normal-donation-list-in-admin-items-select">
              <option value="">All Types</option>
              {DONATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setCurrentPage(1); }} className="normal-donation-list-in-admin-items-select">
              <option value="">All Modes</option>
              {MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="normal-donation-list-in-admin-items-select">
              <option value="5">5 / page</option>
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
            </select>
            <button className="normal-donation-list-in-admin-btn normal-donation-list-in-admin-btn-export" onClick={handleExportExcel} title="Export Filtered Excel">
              <FaFileExport /> Export Excel
            </button>
            <button className="normal-donation-list-in-admin-btn normal-donation-list-in-admin-btn-add" onClick={() => { setShowAddForm(!showAddForm); handleClearDonor(); }}>
              {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Donation</>}
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="normal-donation-list-in-admin-inline-form">
            <div className="normal-donation-list-in-admin-form-header">
               <h3 className="normal-donation-list-in-admin-form-title">{editDonationData ? "✏️ Edit Donation Record" : "📝 Record New Donation (Manual)"}</h3>
            </div>
            
            <h4 className="normal-donation-list-in-admin-section-title">Step 1: Select Donor</h4>
            <div className="normal-donation-list-in-admin-form-grid">
              <div className="normal-donation-list-in-admin-form-group"><label>By Member ID</label><DonorDropdown value={selectedDonor?.memberId || ""} placeholder="Search Member ID" donors={activeDonors} searchKey="memberId" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
              <div className="normal-donation-list-in-admin-form-group"><label>By Name</label><DonorDropdown value={selectedDonor?.name || ""} placeholder="Search Name" donors={activeDonors} searchKey="name" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
              <div className="normal-donation-list-in-admin-form-group"><label>By Mobile No</label><DonorDropdown value={selectedDonor?.mobileNo || ""} placeholder="Search Mobile" donors={activeDonors} searchKey="mobileNo" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
            
              {selectedDonor && (
                <div style={{
                  gridColumn: "1 / -1",
                  padding: "12px",
                  borderRadius: "6px",
                  background: missingProfileFields.length > 0 ? "#fff5f5" : "#f0fdf4",
                  border: `1px solid ${missingProfileFields.length > 0 ? "#feb2b2" : "#bbf7d0"}`,
                  marginTop: "10px",
                  fontSize: "0.85rem"
                }}>
                  {missingProfileFields.length > 0 ? (
                    <span style={{ color: "#c53030", fontWeight: "600" }}>
                      ⚠️ This profile cannot accept donations yet. Please update the donor profile in Donor Management with: {missingProfileFields.join(", ")}.
                    </span>
                  ) : (
                    <span style={{ color: "#2f855a", fontWeight: "600" }}>
                      ✅ Selected Donor profile is fully complete. You can proceed with this donation.
                    </span>
                  )}
                </div>
              )}
            </div>

            <h4 className="normal-donation-list-in-admin-section-title">Step 2: Donation Details</h4>
            <div className="normal-donation-list-in-admin-form-grid">
              <div className="normal-donation-list-in-admin-form-group normal-donation-list-in-admin-col-span-2">
                <label>Donation Type <span className="normal-donation-list-in-admin-req">*</span></label>
                <div className="normal-donation-list-in-admin-radios">
                  {DONATION_TYPES.map((type) => (
                    <label key={type} className={`normal-donation-list-in-admin-radio-label ${form.donationType === type ? "selected" : ""}`}>
                      <input type="radio" name="dType" value={type} checked={form.donationType === type} onChange={(e) => setForm({ ...form, donationType: e.target.value })} /> {type}
                    </label>
                  ))}
                </div>
              </div>

              <div className="normal-donation-list-in-admin-form-group">
                <label>Amount (₹) <span className="normal-donation-list-in-admin-req">*</span></label>
                <input type="text" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/\D/g, "") })} placeholder="Amount" />
              </div>

              <div className="normal-donation-list-in-admin-form-group">
                <label>Date <span className="normal-donation-list-in-admin-req">*</span></label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>

              <div className="normal-donation-list-in-admin-form-group">
                <label>Payment Mode <span className="normal-donation-list-in-admin-req">*</span></label>
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                  {MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
                </select>
              </div>

              <div className="normal-donation-list-in-admin-form-group">
                <label>Receipt No (रसीद नं.)</label>
                <input type="text" value={form.receiptNo} onChange={(e) => setForm({ ...form, receiptNo: e.target.value })} placeholder="Optional Manual Receipt Number" />
              </div>

              {form.mode !== "Cash" && (
                <div className="normal-donation-list-in-admin-form-group">
                  <label>Ref / Cheque No <span className="normal-donation-list-in-admin-req">*</span></label>
                  <input type="text" value={form.refNo} onChange={(e) => setForm({ ...form, refNo: e.target.value })} placeholder="Reference Details" />
                </div>
              )}
            </div>

            <div className="normal-donation-list-in-admin-form-buttons">
              <button className="normal-donation-list-in-admin-btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button className="normal-donation-list-in-admin-btn-confirm" onClick={handleProcessPayment}>{editDonationData ? "Update Donation" : "Submit Donation"}</button>
            </div>
          </div>
        )}

        <div className="normal-donation-list-in-admin-table-wrapper">
          <table className="normal-donation-list-in-admin-table">
            <thead>
              <tr>
                <th className="normal-donation-list-in-admin-col-sno">S.No</th>
                <th className="normal-donation-list-in-admin-col-memberid">Member ID</th>
                <th className="normal-donation-list-in-admin-col-name normal-donation-list-in-admin-align-left">Donor Name</th>
                <th className="normal-donation-list-in-admin-col-fathername">Father Name</th>
                <th className="normal-donation-list-in-admin-col-contact">Contact Number</th>
                <th className="normal-donation-list-in-admin-col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDonors.length === 0 ? (
                  <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No donation records found.</td></tr>
              ) : paginatedDonors.map((donor, index) => {
                const serialNo = (currentPage - 1) * itemsPerPage + index + 1;

                return (
                  <React.Fragment key={donor._id}>
                    <tr className="normal-donation-list-in-admin-main-row">
                      <td className="normal-donation-list-in-admin-col-sno" style={{ fontWeight: "600", color: "#64748b" }}>{serialNo}</td>
                      
                      <td className="normal-donation-list-in-admin-col-memberid" style={{ color: donor.memberId ? "#334155" : "#e67e22", fontWeight: "700" }}>
                          {donor.memberId || 'N/A'}
                      </td>
                      
                      <td className="normal-donation-list-in-admin-col-name normal-donation-list-in-admin-align-left">
                        <div className="normal-donation-list-in-admin-name-wrapper" onClick={() => toggleMainRow(donor._id)}>
                          <div style={{display: 'flex', flexDirection: 'column'}}>
                             <span style={{ fontWeight: 600 }}>{donor.name}</span>
                             <span style={{ fontSize: '0.75rem', color: '#64748b' }} className="normal-donation-list-in-admin-mobile-only-contact-label">{donor.mobileNo || 'N/A'}</span>
                          </div>
                          <button className="normal-donation-list-in-admin-expand-btn">
                            {expandedRow === donor._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>

                      <td className="normal-donation-list-in-admin-col-fathername" style={{ color: "#334155" }}>{donor.fatherName || "N/A"}</td>

                      <td className="normal-donation-list-in-admin-col-contact" style={{ fontWeight: "600", color: "#334155" }}>{donor.mobileNo || "N/A"}</td>
                      
                      <td className="normal-donation-list-in-admin-col-status">
                        <label className="normal-donation-list-in-admin-switch" title={donor.isActive ? "Active" : "Inactive"}>
                          <input type="checkbox" checked={donor.isActive} onChange={() => toggleDonorStatus(donor._id, donor.isActive)} />
                          <span className="normal-donation-list-in-admin-slider normal-donation-list-in-admin-round"></span>
                        </label>
                        <div style={{ fontSize: '0.75rem', marginTop: '4px', color: donor.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                          {donor.isActive ? 'Active' : 'Disabled'}
                        </div>
                      </td>
                    </tr>

                    {expandedRow === donor._id && (
                      <tr className="normal-donation-list-in-admin-expanded-row">
                          <td colSpan="6">
                              <div className="normal-donation-list-in-admin-expanded-content">
                                  
                                  {/* Multi-Breakpoint Summary block */}
                                  <div className="normal-donation-list-in-admin-donor-profile-summary">
                                    <div className="normal-donation-list-in-admin-summary-item normal-donation-list-in-admin-show-lt-768">
                                      <span className="normal-donation-list-in-admin-summary-label">Member ID</span>
                                      <span className="normal-donation-list-in-admin-summary-val">{donor.memberId || 'N/A'}</span>
                                    </div>
                                    
                                    <div className="normal-donation-list-in-admin-summary-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991 normal-donation-list-in-admin-show-991-1200">
                                      <span className="normal-donation-list-in-admin-summary-label">Father's Name</span>
                                      <span className="normal-donation-list-in-admin-summary-val">{donor.fatherName || 'N/A'}</span>
                                    </div>
                                    
                                    <div className="normal-donation-list-in-admin-summary-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991">
                                      <span className="normal-donation-list-in-admin-summary-label">Contact Number</span>
                                      <span className="normal-donation-list-in-admin-summary-val">{donor.mobileNo || 'N/A'}</span>
                                    </div>
                                    
                                    <div className="normal-donation-list-in-admin-summary-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991">
                                      <span className="normal-donation-list-in-admin-summary-label">Status</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                        <label className="normal-donation-list-in-admin-switch">
                                          <input type="checkbox" checked={donor.isActive} onChange={() => toggleDonorStatus(donor._id, donor.isActive)} />
                                          <span className="normal-donation-list-in-admin-slider normal-donation-list-in-admin-round"></span>
                                        </label>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: donor.isActive ? '#e67e22' : '#94a3b8' }}>
                                          {donor.isActive ? 'Active' : 'Disabled'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Subtable list of this donor's Nidhi transactions */}
                                  <div className="normal-donation-list-in-admin-subtable-wrapper">
                                    <table className="normal-donation-list-in-admin-subtable">
                                      <thead>
                                        <tr>
                                          <th className="normal-donation-list-in-admin-subcol-sno">S.No</th>
                                          <th className="normal-donation-list-in-admin-subcol-date">Date & Time</th>
                                          <th className="normal-donation-list-in-admin-subcol-receipt">Receipt No</th>
                                          <th className="normal-donation-list-in-admin-subcol-year">Year</th>
                                          <th className="normal-donation-list-in-admin-subcol-amount">Amount</th>
                                          <th className="normal-donation-list-in-admin-subcol-type">Donation Type</th>
                                          <th className="normal-donation-list-in-admin-subcol-status">Status</th>
                                          <th className="subcol-action">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {donor.normalDonationsList.map((don, idx) => {
                                          const isPGTransaction = Boolean(don.merchantTxnNo) || Boolean(don.pgPaymentId) || Boolean(don.receiptNo && (don.receiptNo.includes('ONL') || don.receiptNo.includes('PUB')));
                                          const year = new Date(don.date).getFullYear();
                                          const isSubExpanded = expandedDonationId === don._id;
                                          
                                          const isCash = don.mode === "Cash";
                                          const hasRef = don.refNo && don.refNo.trim() !== "";
                                          const showRefBlock = isPGTransaction || (!isCash && hasRef);

                                          return (
                                            <React.Fragment key={don._id}>
                                              <tr className="normal-donation-list-in-admin-subtable-row">
                                                <td className="normal-donation-list-in-admin-subcol-sno" data-label="S.No">{idx + 1}</td>
                                                <td className="normal-donation-list-in-admin-subcol-date" data-label="Date & Time">
                                                  <div className="normal-donation-list-in-admin-sub-date-block">
                                                    <div style={{ fontWeight: "600" }}>{new Date(don.date).toLocaleDateString("en-IN")}</div>
                                                    <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                                      {/^[a-fA-F0-9]{24}$/.test(don._id) ? new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' }) : ""}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="normal-donation-list-in-admin-subcol-receipt" data-label="Receipt No">
                                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                    <span style={{ 
                                                      background: '#eff6ff', color: '#3b82f6', 
                                                      padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid #bfdbfe', fontWeight: '700'
                                                    }}>{don.receiptNo || '—'}</span>
                                                    
                                                    <button 
                                                      className="normal-donation-list-in-admin-sub-expand-btn"
                                                      onClick={() => setExpandedDonationId(isSubExpanded ? null : don._id)}
                                                      title="Toggle Transaction Details"
                                                    >
                                                      {isSubExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                                    </button>
                                                  </div>
                                                </td>
                                                <td className="normal-donation-list-in-admin-subcol-year" data-label="Year" style={{ fontWeight: "600" }}>{year}</td>
                                                <td className="normal-donation-list-in-admin-subcol-amount" data-label="Amount" style={{ fontWeight: "700", color: "#e67e22" }}>₹{don.amount}</td>
                                                <td className="normal-donation-list-in-admin-subcol-type" data-label="Donation Type">
                                                  <span className="normal-donation-list-in-admin-badge-info">{don.donationType}</span>
                                                </td>
                                                <td className="normal-donation-list-in-admin-subcol-status" data-label="Status">
                                                  <div className="normal-donation-list-in-admin-sub-toggle-block">
                                                    <label className="normal-donation-list-in-admin-switch">
                                                      <input type="checkbox" checked={don.isActive} onChange={() => toggleDonationStatus(don.donorId, don._id, don.isActive)} />
                                                      <span className="normal-donation-list-in-admin-slider normal-donation-list-in-admin-round"></span>
                                                    </label>
                                                    <div style={{ fontSize: '0.72rem', marginTop: '2px', color: don.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                                                      {don.isActive ? 'Active' : 'Disabled'}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="subcol-action" data-label="Action">
                                                  <div className="normal-donation-list-in-admin-donation-action-btns">
                                                    <button className="normal-donation-list-in-admin-btn-print" onClick={() => navigate("/admin/donation-receipt", { state: { donation: don } })} title="Print Receipt"><FaPrint /> </button>
                                                    
                                                    {isPGTransaction ? (
                                                      <span className="normal-donation-list-in-admin-locked" title="Online Transactions Cannot be Edited"><FaLock /></span>
                                                    ) : (
                                                      <>
                                                        <button className="normal-donation-list-in-admin-btn-edit" onClick={() => handleEditDonation(don)} title="Edit Details"><FaEdit /> </button>
                                                        <button className="normal-donation-list-in-admin-btn-delete" onClick={() => deleteDonation(don.donorId, don._id)} title="Delete Entry"><FaTrashAlt /> </button>
                                                      </>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>

                                              {/* Nested Row rendering variables dynamically matching current screen needs in stunning widgets */}
                                              {isSubExpanded && (
                                                <tr className="normal-donation-list-in-admin-sub-details-row">
                                                  <td colSpan="8">
                                                    <div className={`normal-donation-list-in-admin-sub-details-wrapper ${isPGTransaction ? 'gateway-theme' : 'manual-theme'}`}>
                                                      
                                                      {/* Subtable values dynamic card grid based on display breakpoints */}
                                                      <div className="normal-donation-list-in-admin-pushed-values-grid">
                                                        
                                                        {/* Date & Time card: visible < 991px */}
                                                        <div className="normal-donation-list-in-admin-pushed-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991">
                                                          <strong>Date & Time</strong>
                                                          <span>
                                                            {new Date(don.date).toLocaleDateString("en-IN")}{' '}
                                                            {/^[a-fA-F0-9]{24}$/.test(don._id) && new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}
                                                          </span>
                                                        </div>

                                                        {/* Year card: visible < 768px */}
                                                        <div className="normal-donation-list-in-admin-pushed-item normal-donation-list-in-admin-show-lt-768">
                                                          <strong>Year</strong>
                                                          <span>{year}</span>
                                                        </div>

                                                        {/* Amount card: visible < 768px */}
                                                        <div className="normal-donation-list-in-admin-pushed-item normal-donation-list-in-admin-show-lt-768">
                                                          <strong>Amount</strong>
                                                          <span style={{ fontWeight: '700', color: '#e67e22' }}>₹{don.amount}</span>
                                                        </div>

                                                        {/* Donation Type card: visible < 1200px */}
                                                        <div className="normal-donation-list-in-admin-pushed-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991 normal-donation-list-in-admin-show-991-1200">
                                                          <strong>Donation Type</strong>
                                                          <span className="normal-donation-list-in-admin-badge-info" style={{ marginTop: '4px', width: 'fit-content' }}>{don.donationType}</span>
                                                        </div>

                                                        {/* Payment Mode card: always in expand view */}
                                                        <div className="normal-donation-list-in-admin-pushed-item">
                                                          <strong>Payment Mode</strong>
                                                          <span>{isPGTransaction ? 'Online Gateway' : (don.mode || 'N/A')}</span>
                                                        </div>

                                                        {/* Donation Status card: visible < 1200px */}
                                                        <div className="normal-donation-list-in-admin-pushed-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991 normal-donation-list-in-admin-show-991-1200">
                                                          <strong>Donation Status</strong>
                                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                            <label className="normal-donation-list-in-admin-switch">
                                                              <input type="checkbox" checked={don.isActive} onChange={() => toggleDonationStatus(don.donorId, don._id, don.isActive)} />
                                                              <span className="normal-donation-list-in-admin-slider normal-donation-list-in-admin-round"></span>
                                                            </label>
                                                            <span style={{ fontSize: '0.72rem', fontWeight: '600', color: don.isActive ? '#e67e22' : '#94a3b8' }}>
                                                              {don.isActive ? 'Active' : 'Disabled'}
                                                            </span>
                                                          </div>
                                                        </div>

                                                        {/* Actions card: visible < 1200px */}
                                                        <div className="normal-donation-list-in-admin-pushed-item normal-donation-list-in-admin-show-lt-768 normal-donation-list-in-admin-show-768-991 normal-donation-list-in-admin-show-991-1200">
                                                          <strong>Actions</strong>
                                                          <div className="normal-donation-list-in-admin-donation-action-btns" style={{ justifyContent: 'flex-start', marginTop: '4px' }}>
                                                            <button className="normal-donation-list-in-admin-btn-print" onClick={() => navigate("/admin/donation-receipt", { state: { donation: don } })} title="Print Receipt"><FaPrint /></button>
                                                            {isPGTransaction ? (
                                                              <span className="normal-donation-list-in-admin-locked" title="Online Transactions Cannot be Edited"><FaLock /></span>
                                                            ) : (
                                                              <>
                                                                <button className="normal-donation-list-in-admin-btn-edit" onClick={() => handleEditDonation(don)} title="Edit Details"><FaEdit /> </button>
                                                                <button className="normal-donation-list-in-admin-btn-delete" onClick={() => deleteDonation(don.donorId, don._id)} title="Delete Entry"><FaTrashAlt /></button>
                                                              </>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>

                                                      {/* Transaction gateway or manual reference details. Separated into stunning dashboard widgets */}
                                                      {showRefBlock && (
                                                        <div className="normal-donation-list-in-admin-gateway-ref-box">
                                                          {isPGTransaction ? (
                                                            <div className="normal-donation-list-in-admin-sub-details-grid">
                                                              <div className="normal-donation-list-in-admin-metadata-card">
                                                                <strong>Bank Txn ID</strong>
                                                                <span>{don.refNo || 'N/A'}</span>
                                                              </div>
                                                              <div className="normal-donation-list-in-admin-metadata-card">
                                                                <strong>PG Method</strong>
                                                                <span>{don.pgPaymentMode || 'UPI/Card'}</span>
                                                              </div>
                                                              <div className="normal-donation-list-in-admin-metadata-card">
                                                                <strong>UPI/Card ID</strong>
                                                                <span>{don.pgPaymentId || 'N/A'}</span>
                                                              </div>
                                                              <div className="normal-donation-list-in-admin-metadata-card">
                                                                <strong>Order Ref</strong>
                                                                <span>{don.merchantTxnNo || 'N/A'}</span>
                                                              </div>
                                                            </div>
                                                          ) : (
                                                            <div className="normal-donation-list-in-admin-sub-details-grid manual-details">
                                                              <div className="normal-donation-list-in-admin-metadata-card">
                                                                <strong>Ref / Cheque No</strong>
                                                                <span>{don.refNo || 'N/A'}</span>
                                                              </div>
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}

                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>

                              </div>
                          </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination totalItems={groupedDonors.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>
    </>
  );
};

export default AddNormalDonation;